import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Disc, Info, ListMusic, ListPlus, ListX, Play, Plus, User } from 'lucide-react';
import { PlaylistsContext } from '../../../context/PlaylistsContext';
import { PlayerContext } from '../../../context/PlayerContext';
import { Track } from '../trackList/track';
import './TrackMenu.css';

interface Props {
    track: Track;
    x: number;
    y: number;
    onClose: () => void;
    /** Present only when opened from within a playlist's own track list. */
    onRemoveFromCurrentPlaylist?: () => void;
    currentPlaylistId?: number;
    onShowInfo: (track: Track) => void;
}

export function TrackMenu({
    track,
    x,
    y,
    onClose,
    onRemoveFromCurrentPlaylist,
    currentPlaylistId,
    onShowInfo,
}: Props): React.JSX.Element {
    const { playlists, createPlaylist, addTrackToPlaylist } = useContext(PlaylistsContext);
    const { playTrackNow, addToQueue } = useContext(PlayerContext);
    const navigate = useNavigate();
    const menuRef = useRef<HTMLDivElement>(null);
    const [pendingId, setPendingId] = useState<number | null>(null);
    const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        const handlePointerDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleAdd = async (playlistId: number) => {
        setPendingId(playlistId);
        const ok = await addTrackToPlaylist(playlistId, track.getId());
        setPendingId(null);
        if (ok) {
            setAddedIds((prev) => new Set(prev).add(playlistId));
            setTimeout(onClose, 500);
        }
    };

    const handleCreateAndAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;
        const playlist = await createPlaylist(newName.trim(), '');
        if (playlist) await handleAdd(playlist.id);
    };

    const addablePlaylists = playlists.filter((p) => p.id !== currentPlaylistId);
    const artistId = track.getArtistId();
    const albumId = track.getAlbumId();

    return createPortal(
        <div className="trackMenu" style={{ left: x, top: y }} ref={menuRef}>
            {onRemoveFromCurrentPlaylist && (
                <>
                    <button
                        className="trackMenuItem trackMenuItemDanger"
                        onClick={() => {
                            onRemoveFromCurrentPlaylist();
                            onClose();
                        }}
                    >
                        <ListX size={14} strokeWidth={2.75} />
                        Remove from this playlist
                    </button>
                    <div className="trackMenuDivider" />
                </>
            )}

            <button
                className="trackMenuItem"
                onClick={() => {
                    playTrackNow(track);
                    onClose();
                }}
            >
                <Play size={14} strokeWidth={2.75} />
                Play now
            </button>
            <button
                className="trackMenuItem"
                onClick={() => {
                    addToQueue(track);
                    onClose();
                }}
            >
                <ListPlus size={14} strokeWidth={2.75} />
                Add to queue
            </button>

            {(artistId || albumId) && (
                <>
                    <div className="trackMenuDivider" />
                    {artistId && (
                        <button
                            className="trackMenuItem"
                            onClick={() => {
                                navigate(`/artists/${artistId}`);
                                onClose();
                            }}
                        >
                            <User size={14} strokeWidth={2.75} />
                            Go to artist
                        </button>
                    )}
                    {albumId && (
                        <button
                            className="trackMenuItem"
                            onClick={() => {
                                navigate(`/albums/${albumId}`);
                                onClose();
                            }}
                        >
                            <Disc size={14} strokeWidth={2.75} />
                            Go to album
                        </button>
                    )}
                </>
            )}

            <div className="trackMenuDivider" />

            <div className="trackMenuLabel">Add to playlist</div>
            <div className="trackMenuList">
                {addablePlaylists.length === 0 && !creating && (
                    <div className="trackMenuEmpty">No other playlists yet</div>
                )}
                {addablePlaylists.map((p) => (
                    <button
                        key={p.id}
                        className="trackMenuItem"
                        onClick={() => handleAdd(p.id)}
                        disabled={pendingId === p.id}
                    >
                        <ListMusic size={14} strokeWidth={2.75} />
                        <span className="trackMenuItemLabel">{p.name}</span>
                        {addedIds.has(p.id) && <span className="trackMenuAdded">Added</span>}
                    </button>
                ))}
            </div>

            {creating ? (
                <form className="trackMenuNewForm" onSubmit={handleCreateAndAdd}>
                    <input
                        className="trackMenuNewInput"
                        type="text"
                        placeholder="Playlist name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        maxLength={60}
                        autoFocus
                    />
                </form>
            ) : (
                <button className="trackMenuItem" onClick={() => setCreating(true)}>
                    <Plus size={14} strokeWidth={2.75} />
                    New playlist
                </button>
            )}

            <div className="trackMenuDivider" />
            <button
                className="trackMenuItem"
                onClick={() => {
                    onShowInfo(track);
                    onClose();
                }}
            >
                <Info size={14} strokeWidth={2.75} />
                Track info
            </button>
        </div>,
        document.body
    );
}
